import { Helmet, HelmetData } from "react-helmet-async";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { client } from "../../../client";
import { AI_BG_NEW } from "../../../images";
import { NewFooter } from "../../newFooter";
import useIntersectionObserver from "../../../utils/useIntersectionObserver";
const helmetData = new HelmetData({});

export default function Blogs() {
  const [posts, setPosts] = useState([]);
  const [length, setLength] = useState(6);
  const [featuredPost, setFeaturedPost] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const [sortedPosts, setSortedPosts] = useState([]);

  const targetRef = useRef(null);

  useIntersectionObserver({ targetRef });

  useEffect(() => {
    client
      .fetch(
        `*[_type == "Title"]{
      title,
      author,
      body,
      author_avatar,
      cover_photo,
      tags,
      published_date,
      featured_post,
      id,
      is_africa
    }`
      )
      .then((data) => {
        setPosts(data?.filter((p) => p?.is_africa));
        data?.forEach((p) => {
          if (p?.featured_post && p?.is_africa) {
            setFeaturedPost(p);
          }
        });
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    setSortedPosts(posts?.sort((a, b) => b.id - a.id));
  }, [posts]);

  useEffect(() => {
    if (!searchTerm) {
      setSearchResults([]);
      return;
    }

    const results = posts.filter(
      (post) =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags?.some((tag) =>
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    setSearchResults(results);
  }, [searchTerm, posts]);

  function loadMore() {
    setLength((prev) => {
      const current = prev + 6;
      return current > posts.length ? posts.length : current;
    });
  }

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>Blog | AI &amp; Analytics Insights Africa | Analytics Intelligence Africa</title>
        <link rel="canonical" href="https://africa.analyticsintelligence.com/blog" />
        <meta
          name="description"
          content="Stay ahead with thought leadership and deep technical insights from Analytics Intelligence Africa. Articles, whitepapers, and guides covering AI for analytics, data governance, local language LLMs, and enterprise AI adoption across Africa."
        />
        <meta
          name="keywords"
          content="Analytics Intelligence Africa blog, AI blog Africa, data analytics blog Africa, enterprise AI Africa insights, AI for banking Africa articles, local language LLMs Africa, African AI research, machine learning Africa blog, AI whitepapers Africa, data science Africa, AI thought leadership Africa"
        />
        <meta property="og:title" content="Blog | AI & Analytics Insights Africa | Analytics Intelligence Africa" />
        <meta property="og:description" content="Thought leadership, technical guides, and AI insights from Analytics Intelligence Africa — covering enterprise AI, local language LLMs, and data analytics across Africa." />
        <meta property="og:url" content="https://africa.analyticsintelligence.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Blog | AI & Analytics Insights Africa | Analytics Intelligence Africa" />
        <meta name="twitter:description" content="Thought leadership, technical guides, and AI insights from Analytics Intelligence Africa — covering enterprise AI, local language LLMs, and data analytics across Africa." />
        <meta name="twitter:image" content="https://africa.analyticsintelligence.com/favicon.png" />
      </Helmet>
      <main>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[95vh] flex items-center"
          ref={targetRef}
        >
          <div className="container mx-auto w-full h-full pt-[126px] md:pt-[96px] py-[96px]">
            <div className="flex flex-col items-center gap-4">
              <h2 className="text-[36px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white">
                Articles, Blogs, and Whitepapers
              </h2>
              <div className="mb-8">
                <p className="text-sm md:text-lg text-center font-medium text-[#E3E3E3] leading-5 max-w-[1200px]">
                  Stay ahead of the curve with thought leadership and deep
                  technical insights. Our content library covers:
                </p>
                <p className="text-sm md:text-lg text-center font-medium text-[#E3E3E3] leading-5 max-w-[1200px]">
                  • Advances in AI for analytics and decision science
                </p>
                <p className="text-sm md:text-lg text-center font-medium text-[#E3E3E3] leading-5 max-w-[1200px]">
                  • Practical guides on data governance, privacy, and security
                </p>
                <p className="text-sm md:text-lg text-center font-medium text-[#E3E3E3] leading-5 max-w-[1200px]">
                  • Whitepapers exploring agentic AI frameworks and enterprise
                  adoption strategies
                </p>
              </div>

              <div className="relative w-[380px] px-4 md:px-0">
                <div className="absolute left-[44px] md:left-[20px] top-2/4 -translate-y-2/4 text-white md:text-[#888]">
                  <SearchOutlined fontSize="medium" className="text-white" />
                </div>
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pb-2 pl-16 md:pl-12 border text-base bg-[#FFFFFF33] border-[#FFFFFF33] outline-none rounded-full text-white placeholder:text-white"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white relative pt-[48px] pb-0 overflow-hidden">
          <div className="h-[300px] w-[300px] xl:h-[350px] xl:w-[350px] bg-[#7AA8FF] blur-[180px] absolute top-0 -right-[70%] md:-right-[45%] lg:-right-[15%]"></div>
          <div className="container relative flex flex-col">
            <div className="flex flex-col md:flex-row items-center gap-11">
              {featuredPost?.cover_photo && (
                <div className="basis-2/4 flex items-center justify-center rounded-xl overflow-hidden">
                  <img
                    src={featuredPost?.cover_photo}
                    alt="Data Structures"
                    className="w-full"
                  />
                </div>
              )}

              {featuredPost.id && (
                <div className="basis-2/4 flex flex-col gap-6">
                  <div className="flex justify-between">
                    <div className="flex items-center gap-2">
                      <div className="overflow-hidden rounded-full border-2 border-white">
                        <img
                          src={featuredPost?.author_avatar}
                          alt="avatar"
                          className="w-6 h-6"
                        />
                      </div>
                      <span className="text-black">{featuredPost?.author}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <CalendarTodayOutlinedIcon className="text-black" />
                      <p className="text-black">
                        {new Intl.DateTimeFormat("en-GB", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })
                          .format(new Date(featuredPost?.published_date))
                          .replace(/^(\w+)\s/, "$1, ")}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <h2 className="text-2xl md:text-[32px] leading-[1.2] font-bold text-black">
                      {featuredPost?.title}
                    </h2>
                    <p className="text-sm md:text-base text-[#333333] leading-5">
                      {featuredPost?.body.slice(0, 400)}....
                    </p>
                  </div>

                  <Link to={`/blog/${featuredPost.id}`}>
                    <button className="text-sm md:text-base font-semibold py-3 w-[170px] text-center bg-[#0073FF] hover:bg-[#0073FF]/70 text-white rounded-[100px] gap-2 transition-colors ease-in duration-300">
                      Read More
                    </button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="bg-white relative py-[96px] overflow-hidden">
          <div className="h-[300px] w-[300px] xl:h-[350px] xl:w-[350px] bg-[#7AA8FF] blur-[180px] absolute bottom-0 -right-[70%] md:-right-[45%] lg:-right-[15%]"></div>
          <div className="container relative flex flex-col items-center gap-11">
            {/* <div className="flex flex-col w-full items-start">
              <h2 className="text-black font-extrabold text-[24px] md:text-[32px] text-left">
                Recent Uploads
              </h2>
              <p className="text-sm md:text-base max-w-[1086px] text-center text-[#C0C0C0]">
                Stay informed with our latest insights
              </p>
            </div> */}

            <div className="w-full flex gap-[30px] justify-between flex-wrap z-50">
              {searchResults.length < 1 &&
                sortedPosts.length > 0 &&
                sortedPosts.slice(0, length).map((post) => (
                  <div
                    className="lg:w-[45%] md:w-[48%] xl:max-w-[400px] w-full mx-auto flex flex-col gap-4 p-6 rounded-xl bg-[#FFFFFF0F] flex-shrink-0 shadow-md"
                    key={post?.id}
                  >
                    <div>
                      <img
                        src={post.cover_photo}
                        alt="blog"
                        className="object-cover h-[200px] w-full rounded-lg"
                      />
                    </div>
                    <div className="flex justify-between">
                      <div className="flex items-center gap-2">
                        <div className="overflow-hidden rounded-full border-2 border-white">
                          <img
                            src={post.author_avatar}
                            alt="avatar"
                            className="w-6 h-6"
                          />
                        </div>
                        <span className="text-black text-sm">
                          {post.author}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <CalendarTodayOutlinedIcon
                          fontSize="small"
                          className="text-black"
                        />
                        <p className="text-black text-sm">
                          {new Intl.DateTimeFormat("en-GB", {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })
                            .format(new Date(post.published_date))
                            .replace(/^(\w+)\s/, "$1, ")}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h1 className="text-black text-[18px] leading-[150%] font-bold">
                        {post.title}
                      </h1>

                      <p className="text-[#333333] text-sm leading-[150%]">
                        {post.body.slice(0, 100)}......
                      </p>
                    </div>
                    <Link
                      to={`/blog/${post.id}`}
                      className="text-black text-[12px] font-[500] leading-[150%] w-max self-baseline justify-self-end mt-auto"
                    >
                      <button className="text-sm font-semibold py-1.5 px-4 text-center bg-[#0073FF] hover:bg-[#0073FF]/70 text-white rounded-[100px] gap-2 transition-colors ease-in duration-300">
                        Read Now
                      </button>
                    </Link>
                  </div>
                ))}
            </div>

            <div className="w-full flex gap-[30px] justify-between flex-wrap z-50">
              {searchResults.length > 0 &&
                searchResults.map((post) => (
                  <div
                    className="lg:w-[33%] md:w-[48%] max-w-[400px] w-full mx-auto flex flex-col gap-4 p-6 rounded-xl bg-[#FFFFFF0F] shadow-md flex-shrink-0"
                    key={post?.id}
                  >
                    <div>
                      <img
                        src={post.cover_photo}
                        alt="blog"
                        className="object-cover h-[200px] w-full rounded-lg"
                      />
                    </div>
                    <div className="flex justify-between">
                      <div className="flex items-center gap-2">
                        <div className="overflow-hidden rounded-full border-2 border-white">
                          <img
                            src={post.author_avatar}
                            alt="avatar"
                            className="w-6 h-6"
                          />
                        </div>
                        <span className="text-black text-sm">
                          {post.author}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <CalendarTodayOutlinedIcon
                          fontSize="small"
                          className="text-black"
                        />
                        <p className="text-black text-sm">
                          {new Intl.DateTimeFormat("en-GB", {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })
                            .format(new Date(post.published_date))
                            .replace(/^(\w+)\s/, "$1, ")}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h1 className="text-black text-[18px] leading-[150%] font-bold">
                        {post.title}
                      </h1>

                      <p className="text-[#C0C0C0] text-sm leading-[150%]">
                        {post.body.slice(0, 100)}......
                      </p>
                    </div>
                    <Link
                      to={`/blog/${post.id}`}
                      className="text-black text-[12px] font-[500] leading-[150%] w-max self-baseline justify-self-end mt-auto"
                    >
                      <button className="text-sm font-semibold py-1.5 px-4 text-center bg-[#0073FF] hover:bg-[#0073FF]/70 text-black rounded-[100px] gap-2 transition-colors ease-in duration-300">
                        Read Now
                      </button>
                    </Link>
                  </div>
                ))}
            </div>

            {searchResults.length < 1 && length < posts.length && (
              <button
                className="text-sm md:text-base font-semibold py-3 w-[170px] text-center bg-[#0073FF] hover:bg-[#0073FF]/70 text-white rounded-[100px] gap-2 transition-colors ease-in duration-300"
                onClick={loadMore}
              >
                Load More
              </button>
            )}
          </div>
        </section>
        <NewFooter />
      </main>
    </>
  );
}
